import React from 'react'
import PillButton from './PillButton'
import { HStack } from '@astryxdesign/core/HStack'
import { IconButton } from '@astryxdesign/core/IconButton'
import { TextArea } from '@astryxdesign/core/TextArea'
import { Check, X } from 'lucide-react'

/**
 * A comment a writer leaves on one unit of something the agent produced — a line of a plan, a
 * paragraph of a story block — or on the whole thing. Shared by PlanModal and StoryEditModal.
 */
export interface ReviewComment {
  id: string
  lineIndex: number
  lineText: string
  comment: string
  resolved?: boolean
}

// lineIndex -1 is the sentinel for a comment about the whole plan/passage rather than one unit.
// lineText stays '' for it — there is no single unit to quote.
export const WHOLE_REVIEW_COMMENT_LINE_INDEX = -1

interface ReviewCommentChipProps {
  comment: ReviewComment
  isReadOnly: boolean
  onEdit: () => void
  onRemove: () => void
  className?: string
}

export function ReviewCommentChip({
  comment,
  isReadOnly,
  onEdit,
  onRemove,
  className
}: ReviewCommentChipProps): React.JSX.Element {
  const base = comment.resolved
    ? 'plan-modal-comment-chip plan-modal-comment-chip-resolved'
    : 'plan-modal-comment-chip'
  return (
    <div className={className ? `${base} ${className}` : base}>
      {comment.resolved && (
        <Check size={12} className="plan-modal-comment-resolved-icon" aria-label="Resolved" />
      )}
      <span>
        {comment.comment}
        {!isReadOnly && (
          <button type="button" className="plan-modal-comment-edit-link" onClick={onEdit}>
            Edit
          </button>
        )}
      </span>
      {!isReadOnly && (
        <IconButton
          variant="ghost"
          size="sm"
          icon={<X size={10} />}
          label="Remove comment"
          tooltip="Remove comment"
          onClick={onRemove}
        />
      )}
    </div>
  )
}

interface ReviewCommentEditorProps {
  value: string
  placeholder: string
  onChange: (value: string) => void
  onSave: () => void
  onCancel: () => void
}

export function ReviewCommentEditor({
  value,
  placeholder,
  onChange,
  onSave,
  onCancel
}: ReviewCommentEditorProps): React.JSX.Element {
  return (
    <div className="plan-modal-comment-editor">
      <TextArea
        label="Comment"
        isLabelHidden
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={2}
      />
      <HStack gap={0.5}>
        <PillButton variant="primary" label="Save" onClick={onSave} />
        <PillButton variant="ghost" label="Cancel" onClick={onCancel} />
      </HStack>
    </div>
  )
}
